import { create } from 'zustand';
import axiosClient from '../services/api';

const useSupportStore = create((set, get) => ({
  notifications: [],
  unreadCount: 0,
  faq: [],
  knowledgeBase: [],
  tickets: [],
  settings: null,
  isLoading: false,
  error: null,

  // Notifications actions
  fetchNotifications: async (filters = {}) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams(filters);
      const response = await axiosClient.get(`/notifications?${params.toString()}`);
      const unread = response.data.data.filter(n => !n.read).length;
      set({ 
        notifications: response.data.data, 
        unreadCount: unread,
        isLoading: false 
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch notifications';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  },

  markNotificationAsRead: async (notificationId) => {
    try {
      await axiosClient.put(`/notifications/${notificationId}/read`);
      set((state) => ({
        notifications: state.notifications.map(n => 
          n._id === notificationId ? { ...n, read: true } : n
        ),
        unreadCount: state.unreadCount - 1
      }));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  },

  markAllNotificationsAsRead: async () => {
    try {
      await axiosClient.put('/notifications/read-all');
      set((state) => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0
      }));
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  },

  deleteNotification: async (notificationId) => {
    try {
      await axiosClient.delete(`/notifications/${notificationId}`);
      set((state) => ({
        notifications: state.notifications.filter(n => n._id !== notificationId),
        unreadCount: state.unreadCount - (state.notifications.find(n => n._id === notificationId && !n.read) ? 1 : 0)
      }));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  },

  // Help & Support actions
  fetchFAQ: async (category = 'all') => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get(`/supports/support/faq${category !== 'all' ? `?category=${category}` : ''}`);
      set({ 
        faq: response.data.faqData, 
        isLoading: false 
      });
      return response.data.faqData;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch FAQ';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  },

  fetchKnowledgeBase: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/supports/knowledge-base');
      set({ 
        knowledgeBase: response.data.data, 
        isLoading: false 
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch knowledge base';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  },

  submitSupportTicket: async (ticketData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post('/supports/support/tickets', ticketData);
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to submit support ticket';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  },

  fetchSupportTickets: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/supports/support/tickets');
      set({ 
        tickets: response.data.data, 
        isLoading: false 
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch support tickets';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  },

  // Admin Settings actions
  fetchSystemSettings: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.get('/admin/settings');
      set({ 
        settings: response.data.data, 
        isLoading: false 
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch system settings';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  },

  updateSystemSettings: async (settingsData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.put('/admin/settings', settingsData);
      set({ 
        settings: response.data.data, 
        isLoading: false 
      });
      return response.data.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to update system settings';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  },

  sendTestEmail: async (email) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axiosClient.post('/admin/settings/test-email', { email });
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Failed to send test email';
      set({ 
        error: errorMessage, 
        isLoading: false 
      });
      return null;
    }
  }
}));

export default useSupportStore;